import { useEffect, useState, useContext } from 'react';
import { Block, Blocks } from '@chameleoncreator/creator-app';
import { SCORM } from 'pipwerks-scorm-api-wrapper';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';

import API from '../../../services/scorm/API';
import { NextState, Report } from '../../../store/next';
import {
  findBlocksIn,
  getAllInteractions,
  getAllInteractionsOrdered,
} from '../../../selectors/block';
import { ModuleContext } from 'components/Module/Module_v2';

import pubSub from 'services/pubSub';
import getCurrentLocation from 'services/getCurrentLocation';

const API_KEYS = API;

enum Version {
  OnePointTwo = '1.2',
  TwoThousandAndFour = '2004',
}

enum SuccessString {
  Passed = 'passed',
  Failed = 'failed',
}

enum CompletionString {
  Complete = 'completed',
  Incomplete = 'incomplete',
}

enum ShouldSendScore {
  Always = 'ALWAYS',
  Never = 'NEVER',
  OnComplete = 'COMPLETE',
}

export enum ReportableInteractions {
  Multichoice = 'multichoice',
  Sortation = 'sortation',
}

const getResult = (version, result) => {
  if (version === '1.2') {
    return result ? 'correct' : 'wrong';
  }

  return result ? 'correct' : 'incorrect';
};

/**
 * ExitProps
 *
 * When the module is perparing to exit, it needs to make sure that all the appropriate
 * controls are triggered in the SCORM container. The data in ExitProps is a complete
 * representation of this data and as such can be used to toggle saving behaviours further
 * down the chain
 */
interface ExitProps {
  lastInteractionSeen: boolean;
  allInteractionsSeen: boolean;
  passed: boolean;
  isComplete: boolean;
  scoreRaw: number;
  scoreMax: number;
  sendScore: boolean;
  moduleCompleteInterationRequirement: string;
  moduleCompleteScoreRequirement: boolean;
  shouldSendScoreWhen: string;
}

/**
 * scormFunc
 *
 * A scorm function is a chainable side effect function. It takes some data, possibly performs
 * and action that changes remote data (eg. scorm container function call) updating the props in
 * the process and then returns a new ExitProps object.
 *
 * By chaining these functions we can create easy to follow processes for saving data on exit
 */
type scormFunc = (e: ExitProps) => ExitProps;

/**
 * pipe
 *
 * a custom pipe implementation with type safety for chaining scormFuncs
 *
 * @param fns scormFuncs
 */
const pipe = (...fns: scormFunc[]) => (x: ExitProps) =>
  fns.reduce((v, f) => f(v), x);

/**
 * setScoreData
 *
 * saves the score to scorm container IF required
 */
const setScoreData = (scorm: SCORM, version: string): scormFunc => props => {
  const { scoreRaw, scoreMax, passed, sendScore } = props;
  if (!sendScore) {
    return props;
  }

  scorm.set(API[version].scoreMin, 0);
  scorm.set(API[version].scoreMax, scoreMax);
  scorm.set(API[version].scoreRaw, scoreRaw);

  if (version === Version.TwoThousandAndFour) {
    scorm.set(API[version].scoreScaled, scoreRaw / scoreMax);
    scorm.set(
      API[version].success,
      passed ? SuccessString.Passed : SuccessString.Failed
    );
  }
  return props;
};

const exportedExitProps = {
  exitProps: undefined,
};

/**
 * setSCORMCompletion
 *
 * If the module has been completed, declare it so.
 */
const setSCORMCompletion = (scorm: SCORM, version: string) => (
  isComplete: boolean
): void => {
  // window.console.log(`set: isComplete: ${isComplete}`);
  scorm.set(
    API[version].completion,
    isComplete ? CompletionString.Complete : CompletionString.Incomplete
  );
};

/**
 * setCompletionStatus
 *
 * chainable version of setSCORMCompletion
 */
const setCompletionStatus = (
  scorm: SCORM,
  version: string
): scormFunc => props => {
  const { isComplete } = props;

  setSCORMCompletion(scorm, version)(isComplete);

  return props;
};

/**
 * saveAndQuit
 *
 * shut down the scorm container (should be called last)
 */
const save = (scorm: SCORM): scormFunc => props => {
  scorm.save();

  return props;
};

/**
 * setIsComplete
 *
 * determins whether or not the module is completed based on props
 */
const setIsComplete: scormFunc = props => {
  // window.console.log(props);
  const interactionCompletion =
    props[props.moduleCompleteInterationRequirement];
  const scoreCompletion = props.moduleCompleteScoreRequirement
    ? props.passed
    : true;
  const isComplete = interactionCompletion && scoreCompletion;

  // window.console.log(interactionCompletion);
  // window.console.log(scoreCompletion);
  // window.console.log(isComplete);

  return { ...props, isComplete };
};

/**
 * setShouldSendScore
 *
 * using data provided, decided if we should be sending the score to the scorm container
 * @param props
 */
const setShouldSendScore: scormFunc = props => {
  let shoudlSendScore = false;

  switch (props.shouldSendScoreWhen) {
    case ShouldSendScore.Never:
      shoudlSendScore = false;
      break;
    case ShouldSendScore.Always:
      shoudlSendScore = true;
      break;
    case ShouldSendScore.OnComplete:
      shoudlSendScore = props.isComplete;
      break;
  }

  return {
    ...props,
    sendScore: shoudlSendScore || props.isComplete ? true : false,
  };
};

/**
 * setCompleteOnExitOps
 *
 * the chain of operations for saving
 */
const setCompleteOnExitOps = (
  scorm: SCORM,
  version: string,
  exitProps: ExitProps
) => {
  const funcs = [
    setIsComplete,
    setShouldSendScore,
    setScoreData(scorm, version),
    setCompletionStatus(scorm, version),
    save(scorm),
  ];

  funcs.reduce((cur: ExitProps, next: scormFunc) => {
    const props = next(cur);
    return props;
  }, exitProps);
};

/**
 * q contains all the data that an interaction sends when it wants to report to
 * the scorm container
 */
// interface q {
//   id: string
//   question?: string
//   type: string
//   response: string
//   result: string
// }

type q = Report & { success: string };

type qFunc = (p: q) => void;

// returning early on all of these for testing
const qid: qFunc = ({ id, key }) => {
  SCORM.set(`cmi.interactions.${key}.id`, id);
};

const qdesc: qFunc = ({ key, question }) => {
  SCORM.set(`cmi.interactions.${key}.description`, question);
};

const qtype: qFunc = ({ key, type }) => {
  SCORM.set(`cmi.interactions.${key}.type`, type);
};

const qstudent: qFunc = ({ key, response }) => {
  SCORM.set(`cmi.interactions.${key}.student_response`, response);
};

const qlearner: qFunc = ({ key, response }) => {
  SCORM.set(`cmi.interactions.${key}.learner_response`, response);
};

const qresult: qFunc = ({ key, result }) => {
  SCORM.set(`cmi.interactions.${key}.result`, result);
};

const qsave: qFunc = () => {
  SCORM.save();
};

type InteractionsReportingSequence = {
  [key in Version]: {
    [key in ReportableInteractions]: qFunc[];
  };
};

/**
 * cmiInteractions is a nested map that contains the sequence functions needed
 * to send interaction reporting data to the SCORM container.
 */
const cmiInteractions: InteractionsReportingSequence = {
  [Version.OnePointTwo]: {
    [ReportableInteractions.Multichoice]: [
      qid,
      qtype,
      qstudent,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Sortation]: [qid, qtype, qstudent, qresult, qsave],
  },
  [Version.TwoThousandAndFour]: {
    [ReportableInteractions.Multichoice]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Sortation]: [qid, qtype, qlearner, qresult, qsave],
  },
};

/**
 * setCMIInteractions is an internal function for saving an interaction into the scorm container
 *
 * @param interactionType the type of interaction ( Multichoice | Sortation )
 * @param version version of scorm that we are targeting (1.2 | 2004)
 */
const setCMIInteractions = (
  interactionType: ReportableInteractions,
  version: Version
) => ({ id, question, type, response, result, reportable }: Report) => {
  // return; //bailing from here too for testing
  const count = SCORM.get('cmi.interactions._count');
  const resultParsed = getResult(version, result);

  const qProps: q = {
    key: count,
    id,
    question,
    type,
    reportable,
    response: response ? response : 'pending',
    result: resultParsed as any, // this is BAD
    success: API_KEYS[version].result[result],
  };

  cmiInteractions[version][interactionType].reduce((_, next) => {
    next(qProps);
    return true;
  }, false);
};

/**
 * activeBlockSelector finds blocks that are accessible from the root block
 */
const activeBlockSelector = (blocks: Blocks, rootId: string): Block[] => {
  return findBlocksIn(blocks, blocks[rootId], (i: any) => i);
};

/**
 * scoringBlocks finds all the blocks that are scorable and have it turned on
 */
const scoringBlocks = (blocks: Block[]): string[] => {
  return blocks
    .filter((b: any) => {
      return b?.props?.score && b.props.score === true;
    })
    .map((b: any) => b.id);
};

/**
 * This selector pulls out all the active blocks from the store
 */
const loadActiveBlocks = createSelector(
  [(state: NextState) => state.blocks, (state: NextState) => state.rootBlockId],
  activeBlockSelector
);

const loadScoringBlocks = createSelector(loadActiveBlocks, scoringBlocks);

let loaded = false;

/**
 * This block is responsible for ALL scorm interaction. The core parts are
 *
 * - saving and loading current users progress
 * - saving score and behaviour into the CMI.interactions
 * - setting the score
 * - provide an exit facility ?? (i'm not convinced of this one)
 *
 * @param props
 */

export const SCORMRenderer = (props: Block) => {
  // window.console.log("loading SCORM BLOCK")
  const scormSettings = props.props;

  const {
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
  } = scormSettings as any;

  const dispatch = useDispatch();

  const { passMark } = useContext(ModuleContext);

  const [initialised, setInitialized] = useState(false);
  const [version, setVersion] = useState<Version>(Version.OnePointTwo);

  const [lastInteractionSeen, setLastInteractionSeen] = useState(false);
  const [unloaded, setUnloaded] = useState(false);

  const [reportedInteractions, setReportedInteractions] = useState({});

  const {
    currentTopic,
    interactionsSeen,
    interactionsLocked,
    interactionsReporting,
    scoreData = {},
  } = useSelector((state: NextState) => {
    return {
      currentTopic: state.currentTopic,
      interactionsSeen: state.interactionsSeen,
      interactionsLocked: state.interactionsLocked,
      interactionsReporting: state.userBehaviour.scormResponses,
      scoreData: state.userBehaviour.score,
    };
  });

  const allInteractions = useSelector(getAllInteractionsOrdered);
  const scoringInteractions = useSelector(loadScoringBlocks);
  const numberOfScorableInteractions = scoringInteractions.length;
  const hasScoring = numberOfScorableInteractions > 0;

  useEffect(() => {
    if (!initialised) {
      return;
    }

    // console.log(allInteractions, interactionsSeen);

    if (!allInteractions.length) {
      return;
    }
    if (!interactionsSeen.length) {
      return;
    }

    const lastInteraction = allInteractions[allInteractions.length - 1].id;
    // window.console.log(
    //   lastInteraction,
    //   interactionsSeen,
    //   interactionsSeen.includes(`i${lastInteraction}`)
    // );

    if (interactionsSeen.includes(lastInteraction)) {
      setLastInteractionSeen(true);
    }
  }, [initialised, interactionsSeen, allInteractions]);

  const allInteractionsSeen =
    interactionsSeen &&
    allInteractions &&
    interactionsSeen.length === allInteractions.length;

  const scoreRaw = Object.values(scoreData).reduce<number>(
    (acc, curr) => acc + curr,
    0
  );
  const scorePercent =
    ((scoreRaw || 0) / numberOfScorableInteractions) * 100 || 0;

  const exitProps: ExitProps = {
    lastInteractionSeen,
    allInteractionsSeen,
    scoreRaw,
    scoreMax: numberOfScorableInteractions,
    passed: scorePercent >= Number(passMark),
    isComplete: false,
    sendScore: false,
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
  };

  // dirty exported
  exportedExitProps.exitProps = {
    ...exitProps,
    scorePercent,
  } as any;
  //

  /**
   * call exit on scorm based on the options provided to the block
   */
  const exit = () => {
    if (!initialised) {
      return;
    }

    setCompleteOnExitOps(SCORM, version, exitProps);
  };

  // Initialise SCORM
  useEffect(() => {
    if (!initialised && !loaded) {
      try {
        const scorm = SCORM.init();
        setInitialized(true);
        loaded = true;
      } catch (err) {
        window.console.log('scorm aint happy outside of LMS');
      }

      setVersion((SCORM.version as Version) || Version.OnePointTwo);
    }
  }, [initialised]);

  // LOAD SAVED DATA
  useEffect(() => {
    if (!initialised) {
      return;
    }

    // console.log('LOADING SAVED DATA');

    // force incomplete if not explicitly marked as complete
    const completionStatus = SCORM.get(API_KEYS[version].completion);
    if (completionStatus !== CompletionString.Complete) {
      setSCORMCompletion(SCORM, version)(false);
    }

    //Get the latest suspend data
    const remoteData = SCORM.get(API[version].suspend_data);
    // window.console.log(remoteData);
    let suspendedData = remoteData ? JSON.parse(remoteData) : {};

    // Send loaded data to the store
    if (suspendedData) {
      dispatch({
        type: 'LOAD_SUSPEND_DATA',
        data: suspendedData,
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_SEEN',
        data:
          suspendedData?.interactionsSeen?.length > 0
            ? suspendedData?.interactionsSeen
            : [],
      });
    }

    const reported = Object.keys(
      suspendedData?.interactionsReporting || {}
    ).reduce((k, next: string) => {
      return {
        ...k,
        [next]: true,
      };
    }, {});

    setReportedInteractions(reported);
  }, [initialised]);

  useEffect(() => {
    const un = () => {
      if (unloaded) return;

      exit();
      setUnloaded(true);
    };

    console.log('component mounting');
    window.addEventListener('beforeunload', un);
    window.addEventListener('unload', un);

    return () => {
      console.log('component unmounting');
      window.removeEventListener('beforeunload', un);
      window.removeEventListener('unload', un);
    };
  }, []);

  // SAVE LOCAL DATA ON CHANGES
  useEffect(() => {
    if (!initialised) {
      return;
    }

    // TODO: remove the magic number here
    const delaySetSCORM = setTimeout(() => {
      const suspendData = {
        scoreData,
        interactionsSeen,
        interactionsLocked,
        interactionsReporting,
      };

      SCORM.set(API[version].suspend_data, JSON.stringify(suspendData));
      // SCORM.set(API[version].location, currentTopic);

      setCompleteOnExitOps(SCORM, version, exitProps);
    }, 100);

    return () => {
      clearTimeout(delaySetSCORM);
    };
  }, [
    initialised,
    interactionsSeen,
    interactionsLocked,
    interactionsReporting,
    lastInteractionSeen,
  ]);

  const [ri, setRI] = useState<string[]>([]);

  useEffect(() => {
    if (!initialised) {
      return;
    }

    const completed = Object.keys(reportedInteractions);
    const reported = Object.keys(interactionsReporting);

    const delivered = reported
      .filter(x => !ri.includes(x))
      .filter(x => !completed.includes(x))
      .map(r => {
        const rep = interactionsReporting[r];
        setCMIInteractions(
          rep.reportable as ReportableInteractions,
          version
        )(rep);

        return r;
      });

    setRI([...ri, ...delivered]);
  }, [initialised, interactionsReporting]);

  useEffect(() => {
    // doing the bookmarking when nav happens here. . .
    const pS: any = pubSub.subscribe('SECTION_IN_VIEW', $ => {
      const location = getCurrentLocation() as string;

      if (initialised) {
        SCORM.set(API[version].location, location);
      }

      sessionStorage.setItem('TESTING_LIZARD_BOOKMARKING', location);
    });

    return () => {
      // even though this component never unmounts ( ? )
      pS();
    };
  }, [initialised]);

  return null;
};

export { exportedExitProps };
